import { PhysicsShapeType, Vector3 } from '@babylonjs/core';
import { MovingPlatformConfig } from './behavior/MovingPlatformBehavior';
import { Level } from './Level';
import { PlatformGenerationConfig } from './LevelConfig';
import { AssetObjectConfig, BoxConfig, MeshConfig } from './MeshFactory';
import { Platform, PlatformConfig, PlatformFactory } from './PlatformFactory';

/**
 * Generator for generating platforms inside of a level
 */
export class PlatformGenerator {
  constructor(public level: Level) {}

  async generate(config?: PlatformGenerationConfig): Promise<void> {
    // Create start ground if configured - position it at the start location
    if (config.createStartGround) {
      const startGroundPos = new Vector3(
        this.level.config.path.start.x,
        this.level.config.path.start.y - config.startGroundSize.y / 2 - 1, // Just below player start
        this.level.config.path.start.z
      );
      if (config.startGroundAsset) {
        const mesh = AssetObjectConfig.builder()
          .object(config.startGroundAsset.assetObjects[0])
          .size(config.startGroundSize)
          .physicsShapeType(PhysicsShapeType.MESH)
          .build();
        const platform = PlatformFactory.create(
          {
            mesh,
            position: this.level.config.path.start,
          },
          this.level,
          0
        );
        this.level.platforms.push(platform);
      } else {
        this.createStaticPlatform(startGroundPos, config.startGroundSize, config.platformMaterial, 0);
      }
    }

    // Generate platforms along the path
    this.generatePlatformsAlongPath(config);

    // Create goal platform at destination
    if (config.createDestinationGround) {
      if (config.destinationGroundAsset) {
        const mesh = AssetObjectConfig.builder()
          .object(config.destinationGroundAsset.assetObjects[0])
          .size(config.destinationGroundSize)
          .physicsShapeType(PhysicsShapeType.MESH)
          .build();

        const platform = PlatformFactory.create(
          {
            mesh,
            position: this.level.config.path.destination,
          },
          this.level,
          this.level.pathPoints.length - 1
        );
        this.level.platforms.push(platform);
      } else {
        this.createStaticPlatform(
          this.level.config.path.destination,
          config.destinationGroundSize,
          config.platformMaterial,
          this.level.pathPoints.length - 1
        );
      }
    }
  }

  private createStaticPlatform(position: Vector3, size: Vector3, materialIndexOrName: number | string, pathIndex: number): Platform {
    const material = this.level.getMaterial(materialIndexOrName);
    const platform = PlatformFactory.create(
      {
        mesh: BoxConfig.builder().size(size).material(material).build(),
        position,
      },
      this.level,
      pathIndex
    );
    this.level.platforms.push(platform);
    return platform;
  }

  /**
   * Generate platforms along the path based on configuration rules
   */
  private generatePlatformsAlongPath(config: PlatformGenerationConfig): void {
    const pathLength = this.level.pathPoints.length;
    let platformIndex = 0;

    // Skip first path points if start ground is enabled to avoid overlap
    const startIndex = config.createStartGround ? Math.ceil(config.platformInterval) : 0;
    const endIndex = config.createDestinationGround ? pathLength - Math.ceil(config.platformInterval) : pathLength;

    for (let i = startIndex; i < endIndex; i += config.platformInterval) {
      const pathPoint = this.level.pathPoints[i];
      if (!pathPoint) continue;

      // Calculate progression factor (0 at start, 1 at end)
      const progression = i / pathLength;

      // Calculate platform position with variance
      const position = this.calculatePlatformPosition(pathPoint, i, progression, config, platformIndex);

      // Calculate platform size with random variation
      const size = this.calculatePlatformSize(config);

      // Apply difficulty progression to size (smaller platforms further along)
      if (config.difficultyProgression) {
        const shrinkFactor = 1 - progression * (1 - 1 / config.difficultyMultiplier);
        size.x *= shrinkFactor;
        size.z *= shrinkFactor;
      }

      const moving = this.shouldCreatePlatformType(platformIndex, progression, config.movingPlatformChance, 2, undefined, 0.5);
      const crumbling =
        !moving && this.shouldCreatePlatformType(platformIndex, progression, config.crumblingPlatformChance, 3, undefined, 0.8);
      const isStatic = !moving && !crumbling;

      let meshConfig: MeshConfig;
      let platformConfig: PlatformConfig = {
        mesh: undefined,
        position: position,
      };

      if (isStatic) {
        if (config.platformAsset) {
          const index = this.level.integerInRange(0, config.platformAsset.assetObjects.length - 1);
          meshConfig = AssetObjectConfig.builder()
            .object(config.platformAsset.assetObjects[index].withClone(true))
            .size(size.scale(config.platformAsset.assetObjects[index].scale))
            .physicsShapeType(PhysicsShapeType.MESH)
            .build();
        } else {
          meshConfig = BoxConfig.builder().size(size).material(this.level.getMaterial(config.platformMaterial)).build();
        }
      }

      if (moving) {
        // Calculate movement path first
        const { start, end } = this.calculateMovingPlatformPath(position, i, config);
        const movingConfig: MovingPlatformConfig = {
          start,
          end,
          speed: this.level.floatInRange(config.movingPlatformSpeed.min, config.movingPlatformSpeed.max),
        };
        platformConfig.moving = movingConfig;

        // Then create mesh
        if (config.movingPlatformAsset) {
          const index = this.level.integerInRange(0, config.movingPlatformAsset.assetObjects.length - 1);
          meshConfig = AssetObjectConfig.builder()
            .object(config.movingPlatformAsset.assetObjects[index].withClone(true))
            .size(size.scale(config.movingPlatformAsset.scale))
            .physicsShapeType(PhysicsShapeType.MESH)
            .build();
        } else {
          meshConfig = BoxConfig.builder().size(size).material(this.level.getMaterial(config.movingPlatformMaterial)).build();
          // For box moving platforms, we usually want to start at the calculated start position if we don't have a specific asset offset logic
          // But the behavior handles setting the position to 'start' on attach.
          // However, PlatformFactory only uses config.position if provided.
          // Let's ensure platformConfig.position is undefined for moving platforms so the behavior controls it,
          // OR set it to 'start' initially. behavior.attach() does set it to start.
          platformConfig.position = undefined;
        }
      }

      if (crumbling) {
        platformConfig.crumbling = config.crumblingPlatformConfig;

        if (config.crumblingPlatformAsset) {
          const index = this.level.integerInRange(0, config.crumblingPlatformAsset.assetObjects.length - 1);
          meshConfig = AssetObjectConfig.builder()
            .object(config.crumblingPlatformAsset.assetObjects[index].withClone(true))
            .size(size.scale(config.crumblingPlatformAsset.scale))
            .physicsShapeType(PhysicsShapeType.MESH)
            .build();
        } else {
          meshConfig = BoxConfig.builder().size(size).material(this.level.getMaterial(config.crumblingPlatformMaterial)).build();
        }
      }

      platformConfig.mesh = meshConfig;

      const platform = PlatformFactory.create(platformConfig, this.level, i);
      this.level.platforms.push(platform);

      platformIndex++;
    }
  }

  /**
   * Calculate the final position for a platform based on path point and variance rules
   */
  private calculatePlatformPosition(
    pathPoint: Vector3,
    pathIndex: number,
    progression: number,
    config: PlatformGenerationConfig,
    platformIndex: number
  ): Vector3 {
    // Get height variance, increased by difficulty progression
    let heightVarianceMax = config.heightVariance.max;
    let heightVarianceMin = config.heightVariance.min;

    // Reduce height variance for first few platforms to ensure reachability
    const rampUpPlatforms = 5;
    if (platformIndex < rampUpPlatforms) {
      const rampFactor = platformIndex / rampUpPlatforms;
      heightVarianceMax *= rampFactor;
      heightVarianceMin = Math.max(heightVarianceMin, -0.2); // Minimal drops for first platforms
    }

    if (config.difficultyProgression) {
      heightVarianceMax *= 1 + progression * (config.difficultyMultiplier - 1);
    }

    const heightOffset = this.level.floatInRange(heightVarianceMin, heightVarianceMax);
    const lateralOffset = this.level.floatInRange(config.lateralOffset.min, config.lateralOffset.max);

    // Get direction perpendicular to path for lateral offset
    const nextPoint = this.level.pathPoints[pathIndex + 1] ?? pathPoint;
    const prevPoint = this.level.pathPoints[pathIndex - 1] ?? pathPoint;
    const tangent = nextPoint.subtract(prevPoint).normalize();
    const lateral = new Vector3(-tangent.z, 0, tangent.x); // Perpendicular in XZ plane

    // For first platforms, blend Y position from start height to path height
    let yPosition = pathPoint.y + heightOffset;
    if (platformIndex < rampUpPlatforms) {
      const rampFactor = platformIndex / rampUpPlatforms;
      const startY = this.level.config.path.start.y + platformIndex * 0.5; // Gentle rise from start
      yPosition = startY + (pathPoint.y - startY) * rampFactor + heightOffset;
    }

    return new Vector3(
      config.pathOffset.x + pathPoint.x + lateral.x * lateralOffset,
      config.pathOffset.y + yPosition,
      config.pathOffset.z + pathPoint.z + lateral.z * lateralOffset
    );
  }

  /**
   * Calculate random platform size within configured bounds
   */
  private calculatePlatformSize(config: PlatformGenerationConfig): Vector3 {
    return new Vector3(
      this.level.floatInRange(config.platformSize.min.x, config.platformSize.max.x),
      this.level.floatInRange(config.platformSize.min.y, config.platformSize.max.y),
      this.level.floatInRange(config.platformSize.min.z, config.platformSize.max.z)
    );
  }

  /**
   * Determine if a platform should be a specific platform
   */
  private shouldCreatePlatformType(
    platformIndex: number,
    progression: number,
    chance: number,
    minPlatformIndex?: number,
    maxPlatformIndex?: number,
    difficultyProgressionFactor?: number
  ): boolean {
    // Don't create at the very start
    if (platformIndex !== undefined && platformIndex < minPlatformIndex) return false;
    // Don't create at the very end
    if (platformIndex !== undefined && platformIndex > maxPlatformIndex) return false;

    // Increase chance with progression if difficulty progression is enabled
    if (difficultyProgressionFactor !== undefined) {
      chance *= 1 + progression * difficultyProgressionFactor; // Up to x% more likely at the end
    }

    return this.level.rng.uFloat32() < chance;
  }

  /**
   * Calculate start and end positions for a moving platform
   */
  private calculateMovingPlatformPath(
    basePosition: Vector3,
    pathIndex: number,
    config: PlatformGenerationConfig
  ): { start: Vector3; end: Vector3 } {
    const distance = this.level.floatInRange(config.movingPlatformDistance.min, config.movingPlatformDistance.max);

    // Get path direction and create perpendicular movement
    const nextPoint = this.level.pathPoints[pathIndex + 1] ?? basePosition;
    const prevPoint = this.level.pathPoints[pathIndex - 1] ?? basePosition;
    const tangent = nextPoint.subtract(prevPoint).normalize();

    // Randomly choose horizontal (perpendicular) or vertical movement
    const isVertical = this.level.rng.uFloat32() > 0.5;

    if (isVertical) {
      return {
        start: basePosition.clone(),
        end: new Vector3(basePosition.x, basePosition.y + distance, basePosition.z),
      };
    } else {
      const lateral = new Vector3(-tangent.z, 0, tangent.x);
      const halfDistance = distance / 2;
      return {
        start: basePosition.add(lateral.scale(-halfDistance)),
        end: basePosition.add(lateral.scale(halfDistance)),
      };
    }
  }
}
